import { useCallback, useState } from "react";

import dayjs from "dayjs";

import useInterval from "./use-interval";

export const expiryTimestamp = (ts) => new Date(ts).getTime() > 0;

export const onTimerExpire = (onExpire) =>
  onExpire && typeof onExpire === "function";

export const getTimeFromSeconds = (secs) => {
  const totalSeconds = Math.ceil(secs);
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return {
    seconds,
    minutes,
    hours,
    days,
  };
};

export const getSecondsFromExpiry = (expiry, shouldRound) => {
  const now = new Date().getTime();
  const milliSecondsDistance = expiry - now;
  if (milliSecondsDistance > 0) {
    const val = milliSecondsDistance / 1000;
    return shouldRound ? Math.round(val) : val;
  }
  return 0;
};

const DEFAULT_DELAY = 1000;
function getDelayFromExpiryTimestamp(expiryTs) {
  if (!expiryTimestamp(expiryTs)) {
    return null;
  }

  const seconds = getSecondsFromExpiry(expiryTs);
  const extraMilliSeconds = Math.floor((seconds - Math.floor(seconds)) * 1000);
  return extraMilliSeconds > 0 ? extraMilliSeconds : DEFAULT_DELAY;
}

export default function useRangeTimer({
  startTimestamp,
  expiryTimestamp: expiry,
  onTimeExpire,
  onRangeChange,
  startTimestampMargin = 600, // Seconds
  endTimestampMargin = 600, // Seconds
  onMarginRangeChange,
  autoStart = true,
}) {
  const [expiryTimestamp, setExpiryTimestamp] = useState(expiry);
  const [seconds, setSeconds] = useState(getSecondsFromExpiry(expiryTimestamp));
  const [isRunning, setIsRunning] = useState(autoStart);
  const [didStart, setDidStart] = useState(autoStart);
  const [isExpired, setIsExpired] = useState(
    getSecondsFromExpiry(expiryTimestamp) <= 0
  );
  const [isInRange, setIsInRange] = useState(false);
  const [isInMarginRange, setIsInMarginRange] = useState(false);
  const [delay, setDelay] = useState(
    getDelayFromExpiryTimestamp(expiryTimestamp)
  );

  const handleExpire = useCallback(() => {
    onTimerExpire(onTimeExpire) && onTimeExpire();
    setIsRunning(false);
    setIsExpired(true);
    setDelay(null);
  }, [onTimeExpire]);

  const pause = useCallback(() => {
    setIsRunning(false);
  }, []);

  const restart = useCallback((newExpiryTimestamp, newAutoStart = true) => {
    setDelay(getDelayFromExpiryTimestamp(newExpiryTimestamp));
    setDidStart(newAutoStart);
    setIsRunning(newAutoStart);
    setExpiryTimestamp(newExpiryTimestamp);
    setSeconds(getSecondsFromExpiry(newExpiryTimestamp));
  }, []);

  const resume = useCallback(() => {
    const time = new Date();
    time.setMilliseconds(time.getMilliseconds() + seconds * 1000);
    restart(time);
  }, [seconds, restart]);

  const start = useCallback(() => {
    if (didStart) {
      setSeconds(getSecondsFromExpiry(expiryTimestamp));
      setIsRunning(true);
    } else {
      resume();
    }
  }, [expiryTimestamp, didStart, resume]);

  useInterval(
    () => {
      const currentTimestamp = parseInt(dayjs().format("x"), 10);
      setIsInMarginRange((prev) => {
        const current =
          startTimestamp - startTimestampMargin * 1000 < currentTimestamp &&
          currentTimestamp < expiryTimestamp + endTimestampMargin * 1000;
        if (prev !== current && typeof onMarginRangeChange === "function") {
          onMarginRangeChange(
            current,
            currentTimestamp,
            startTimestamp,
            expiryTimestamp
          );
        }

        return current;
      });

      setIsInRange((prev) => {
        const current =
          startTimestamp < currentTimestamp &&
          currentTimestamp < expiryTimestamp;
        if (prev !== current && typeof onRangeChange === "function") {
          onRangeChange(
            current,
            currentTimestamp,
            startTimestamp,
            expiryTimestamp
          );
        }

        return current;
      });

      if (delay !== DEFAULT_DELAY) {
        setDelay(DEFAULT_DELAY);
      }

      const secondsValue = getSecondsFromExpiry(expiryTimestamp);
      setSeconds(secondsValue);
      if (secondsValue <= 0) {
        handleExpire();
      }
    },
    isRunning ? delay : null
  );

  return {
    ...getTimeFromSeconds(seconds),
    start,
    pause,
    resume,
    restart,
    isRunning,
    isExpired,
    isInRange,
    isInMarginRange,
    setExpiryTimestamp,
  };
}
